import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner } from '../components/common/ErrorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Edit, Trash2, AlertCircle, Download, Wallet, DollarSign, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

// Settlement Dashboard with Professional Gradient Cards
const SettlementDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/api/settlement/dashboard');
      setDashboard(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 text-xs sm:text-sm font-medium">Net Profit (Kitty Points)</p>
            <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{(dashboard?.net_profit || 0).toLocaleString()}</h3>
            <p className="text-xs text-green-200 mt-1">{dashboard?.profit_summaries_count || 0} summaries</p>
          </div>
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-xs sm:text-sm font-medium">Total Settled</p>
            <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{(dashboard?.total_settled || 0).toLocaleString()}</h3>
            <p className="text-xs text-blue-200 mt-1">{dashboard?.settlements_count || 0} settlements</p>
          </div>
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Wallet className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-red-100 text-xs sm:text-sm font-medium">Total Pending</p>
            <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{(dashboard?.total_pending || 0).toLocaleString()}</h3>
          </div>
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-purple-100 text-xs sm:text-sm font-medium">Owners</p>
            <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{dashboard?.owners_count || 0}</h3>
          </div>
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Owner Management
const OwnerManagement = () => {
  const [ownersData, setOwnersData] = useState({ owners: [], total_share: 0, remaining_share: 100 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ owner_name: '', share_percentage: '' });
  const [editId, setEditId] = useState(null);

  useEffect(() => { fetchOwners(); }, []);

  const fetchOwners = async () => {
    try {
      const response = await api.get('/api/settlement/owners');
      setOwnersData(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/api/settlement/owners/${editId}`, formData);
        toast.success('Owner updated successfully');
      } else {
        await api.post('/api/settlement/owners', { ...formData, share_percentage: parseFloat(formData.share_percentage) });
        toast.success('Owner created successfully');
      }
      setShowForm(false);
      setEditId(null);
      setFormData({ owner_name: '', share_percentage: '' });
      fetchOwners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save owner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    try {
      await api.delete(`/api/settlement/owners/${id}`);
      toast.success('Owner deleted');
      fetchOwners();
    } catch (err) {
      toast.error('Failed to delete owner');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {ownersData.total_share !== 100 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="text-amber-800 font-medium">
              Warning: Total share is currently {ownersData.total_share}%. Add {ownersData.remaining_share}% more to complete.
            </span>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button data-testid="add-owner-btn" onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="w-4 h-4" />Add Owner
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Owner Name</TableHead>
                  <TableHead>Share %</TableHead>
                  <TableHead>Total Entitled</TableHead>
                  <TableHead>Total Paid</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ownersData.owners.map((owner, i) => (
                  <TableRow key={owner.id} data-testid={`owner-row-${i}`}>
                    <TableCell className="font-medium">{i + 1}</TableCell>
                    <TableCell className="font-semibold">{owner.owner_name}</TableCell>
                    <TableCell>{owner.share_percentage}%</TableCell>
                    <TableCell>{(owner.total_entitled || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-green-600 font-medium">{(owner.total_paid || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className={owner.pending > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-medium'}>
                      {(owner.pending || 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={owner.status === 'SETTLED' ? 'success' : 'destructive'}>
                        {owner.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`edit-owner-${i}`}
                          onClick={() => {
                            setEditId(owner.id);
                            setFormData({ owner_name: owner.owner_name, share_percentage: owner.share_percentage.toString() });
                            setShowForm(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`delete-owner-${i}`}
                          onClick={() => handleDelete(owner.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {ownersData.owners.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No owners added yet. Click "Add Owner" to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit' : 'Add'} Owner</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Owner Name *</Label>
              <Input
                data-testid="owner-name-input"
                value={formData.owner_name}
                onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                required
                placeholder="Enter owner name"
              />
            </div>
            <div>
              <Label>Share Percentage (%) *</Label>
              <Input
                data-testid="share-percentage-input"
                type="number"
                min="0"
                max={editId ? 100 : ownersData.remaining_share}
                step="0.01"
                value={formData.share_percentage}
                onChange={(e) => setFormData({...formData, share_percentage: e.target.value})}
                required
                placeholder="Enter share percentage"
              />
              <p className="text-xs text-gray-500 mt-1">Available share: {ownersData.remaining_share}%</p>
            </div>
            <Button type="submit" className="w-full" data-testid="submit-owner-btn">
              {editId ? 'Update' : 'Add'} Owner
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Profit Summary Management with FIXED CALCULATION
const ProfitSummaryManagement = () => {
  const [summariesData, setSummariesData] = useState({ summaries: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ from_date: '', to_date: '', gross_profit: '', total_expenses: '' });

  useEffect(() => { fetchSummaries(); }, []);

  const fetchSummaries = async () => {
    try {
      const response = await api.get('/api/settlement/profit-summaries');
      setSummariesData(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/settlement/profit-summaries', {
        ...formData,
        gross_profit: parseFloat(formData.gross_profit),
        total_expenses: parseFloat(formData.total_expenses)
      });
      toast.success('Profit summary created successfully');
      setShowForm(false);
      setFormData({ from_date: '', to_date: '', gross_profit: '', total_expenses: '' });
      fetchSummaries();
    } catch (err) {
      toast.error('Failed to create profit summary');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deleting this profit summary will affect all related settlements. Continue?')) return;
    try {
      await api.delete(`/api/settlement/profit-summaries/${id}`);
      toast.success('Profit summary deleted');
      fetchSummaries();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // ✅ FIXED: Apply 1000 ₹ = 1 kitty point conversion
  const netProfit = parseFloat(formData.gross_profit || 0) - (parseFloat(formData.total_expenses || 0) / 1000);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs sm:text-sm font-medium">Total Gross Profit</p>
              <p className="text-2xl sm:text-3xl font-bold text-white mt-2">{(summariesData.totals?.gross_profit || 0).toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-xs sm:text-sm font-medium">Total Expenses (₹)</p>
              <p className="text-2xl sm:text-3xl font-bold text-white mt-2">{(summariesData.totals?.total_expenses || 0).toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs sm:text-sm font-medium">Total Net Profit</p>
              <p className="text-2xl sm:text-3xl font-bold text-white mt-2">{(summariesData.totals?.net_profit || 0).toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="create-profit-summary-btn" onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="w-4 h-4" />Create Profit Summary
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Gross Profit (KP)</TableHead>
                  <TableHead>Expenses (₹)</TableHead>
                  <TableHead>Net Profit (KP)</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summariesData.summaries.map((s, i) => (
                  <TableRow key={s.id} data-testid={`profit-summary-row-${i}`}>
                    <TableCell className="font-medium">{s.from_date} → {s.to_date}</TableCell>
                    <TableCell className="text-green-600 font-semibold">{(s.gross_profit || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-red-600 font-semibold">₹{(s.total_expenses || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="font-bold text-blue-600">{(s.net_profit || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell>{new Date(s.created_at).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`delete-profit-${i}`}
                        onClick={() => handleDelete(s.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {summariesData.summaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No profit summaries yet. Click "Create Profit Summary" to add one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Profit Summary</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Date *</Label>
                <Input
                  data-testid="from-date-input"
                  type="date"
                  value={formData.from_date}
                  onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>To Date *</Label>
                <Input
                  data-testid="to-date-input"
                  type="date"
                  value={formData.to_date}
                  onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Gross Profit (Kitty Points) *</Label>
              <Input
                data-testid="gross-profit-input"
                type="number"
                min="0"
                step="0.01"
                value={formData.gross_profit}
                onChange={(e) => setFormData({...formData, gross_profit: e.target.value})}
                required
                placeholder="Enter gross profit in kitty points"
              />
            </div>
            <div>
              <Label>Total Expenses (₹) *</Label>
              <Input
                data-testid="total-expenses-input"
                type="number"
                min="0"
                step="0.01"
                value={formData.total_expenses}
                onChange={(e) => setFormData({...formData, total_expenses: e.target.value})}
                required
                placeholder="Enter total expenses in rupees"
              />
              <p className="text-xs text-gray-500 mt-1">📌 Note: 1000 ₹ expense = 1 kitty point</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-600 mb-1">Formula: Net Profit = Gross Profit − (Expenses ÷ 1000)</p>
              <p className="text-2xl font-bold text-blue-900" data-testid="calculated-net-profit">{netProfit.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Distributable Kitty Points</p>
            </div>
            <Button type="submit" className="w-full" data-testid="save-profit-summary-btn">Save Profit Summary</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Settlement Payments
const SettlementPayments = () => {
  const [payments, setPayments] = useState([]);
  const [owners, setOwners] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formData, setFormData] = useState({
    owner_id: '',
    profit_period: '',
    payment_amount: '',
    payment_mode: 'Cash',
    payment_date: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  const [editData, setEditData] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, ownersRes, summariesRes] = await Promise.all([
        api.get('/api/settlement/payments'),
        api.get('/api/settlement/owners'),
        api.get('/api/settlement/profit-summaries')
      ]);
      setPayments(paymentsRes.data);
      setOwners(ownersRes.data.owners || []);
      setSummaries(summariesRes.data.summaries || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerSelect = (ownerId) => {
    setFormData({ ...formData, owner_id: ownerId, payment_amount: '' });
    setSelectedOwner(owners.find(o => o.id === ownerId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/settlement/payments', {
        ...formData,
        payment_amount: parseFloat(formData.payment_amount)
      });
      toast.success('Settlement payment recorded successfully');
      setShowForm(false);
      setFormData({
        owner_id: '',
        profit_period: '',
        payment_amount: '',
        payment_mode: 'Cash',
        payment_date: new Date().toISOString().split('T')[0],
        remarks: ''
      });
      setSelectedOwner(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/settlement/payments/${editData.id}`, {
        remarks: editData.remarks
      });
      toast.success('Settlement remarks updated successfully');
      setShowEditForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update settlement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this settlement payment?')) return;
    try {
      await api.delete(`/api/settlement/payments/${id}`);
      toast.success('Payment deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const pendingOwners = owners.filter(o => o.pending > 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Pending Summary */}
      {pendingOwners.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Pending Settlements</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pendingOwners.map(owner => (
              <div key={owner.id} className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-4 shadow-lg">
                <p className="font-semibold text-lg text-white">{owner.owner_name} <span className="text-sm font-normal text-red-100">({owner.share_percentage}%)</span></p>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="text-red-100">Entitled: <span className="font-medium text-white">{(owner.total_entitled || 0).toLocaleString('en-IN')}</span></p>
                  <p className="text-red-100">Paid: <span className="font-medium text-white">{(owner.total_paid || 0).toLocaleString('en-IN')}</span></p>
                  <p className="text-base font-bold text-white mt-2">Pending: {(owner.pending || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="create-settlement-btn"
          onClick={() => setShowForm(true)}
          disabled={pendingOwners.length === 0}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />Pay to owner
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settlement History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Owner</TableHead>
                  <TableHead>Profit Period</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment Mode</TableHead>
                  <TableHead>Paid On</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p, i) => (
                  <TableRow key={p.id} data-testid={`payment-row-${i}`}>
                    <TableCell className="font-semibold">{p.owner_name}</TableCell>
                    <TableCell>{p.profit_period}</TableCell>
                    <TableCell className="text-green-600 font-bold">{(p.payment_amount || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{p.payment_mode}</Badge>
                    </TableCell>
                    <TableCell>{new Date(p.payment_date).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell className="text-gray-600">{p.remarks || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`edit-payment-${i}`}
                          onClick={() => {
                            setEditData(p);
                            setShowEditForm(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`delete-payment-${i}`}
                          onClick={() => handleDelete(p.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No settlement payments yet. Create your first settlement.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Settlement Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Settlement Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Owner *</Label>
              <Select value={formData.owner_id} onValueChange={handleOwnerSelect}>
                <SelectTrigger data-testid="owner-select">
                  <SelectValue placeholder="Select owner with pending amount" />
                </SelectTrigger>
                <SelectContent>
                  {pendingOwners.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.owner_name} (Pending: {o.pending?.toLocaleString('en-IN')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Profit Period *</Label>
              <Select value={formData.profit_period} onValueChange={(v) => setFormData({...formData, profit_period: v})}>
                <SelectTrigger data-testid="period-select">
                  <SelectValue placeholder="Select profit period" />
                </SelectTrigger>
                <SelectContent>
                  {summaries.map(s => (
                    <SelectItem key={s.id} value={`${s.from_date} - ${s.to_date}`}>
                      {s.from_date} → {s.to_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Amount (Kitty Points) *</Label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                min="0"
                max={selectedOwner?.pending || undefined}
                step="0.01"
                value={formData.payment_amount}
                onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                required
                placeholder="Enter payment amount"
              />
              {selectedOwner && (
                <p className="text-xs text-gray-500 mt-1">Maximum: {selectedOwner.pending?.toLocaleString('en-IN')}</p>
              )}
            </div>

            <div>
              <Label>Payment Mode *</Label>
              <Select value={formData.payment_mode} onValueChange={(v) => setFormData({...formData, payment_mode: v})}>
                <SelectTrigger data-testid="payment-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Date *</Label>
              <Input
                data-testid="payment-date-input"
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                data-testid="remarks-input"
                value={formData.remarks}
                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                placeholder="Add any notes or remarks"
                rows={3}
              />
            </div>

            <Button type="submit" className="w-full" data-testid="pay-btn">Record Payment</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Settlement Dialog - Only Remarks Editable */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Settlement</DialogTitle>
          </DialogHeader>
          {editData && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-amber-800">Only Remarks can be edited</span>
              </div>

              <div>
                <Label>Owner (Locked)</Label>
                <Input value={editData.owner_name} disabled className="bg-gray-100" />
              </div>

              <div>
                <Label>Amount (Locked)</Label>
                <Input value={`${editData.payment_amount?.toLocaleString('en-IN')}`} disabled className="bg-gray-100" />
              </div>

              <div>
                <Label>Period (Locked)</Label>
                <Input value={editData.profit_period} disabled className="bg-gray-100" />
              </div>

              <div>
                <Label>Remarks *</Label>
                <Textarea
                  data-testid="edit-remarks-input"
                  value={editData.remarks || ''}
                  onChange={(e) => setEditData({...editData, remarks: e.target.value})}
                  placeholder="Edit remarks"
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="update-payment-btn">Update</Button>
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditForm(false)}>Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reports
const SettlementReports = () => {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await api.get('/api/settlement/reports', { params });
      setReports(response.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/api/settlement/export/excel', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `settlement_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Excel report downloaded successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !reports) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>From Date (Optional)</Label>
              <Input
                data-testid="report-from-date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label>To Date (Optional)</Label>
              <Input
                data-testid="report-to-date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <Button
              data-testid="generate-report-btn"
              onClick={fetchReports}
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
            <Button
              data-testid="export-excel-btn"
              onClick={exportExcel}
              disabled={!reports || exporting}
              variant="outline"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {reports && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs sm:text-sm font-medium">Gross Profit</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2">{(reports.summary?.total_gross_profit || 0).toLocaleString()}</h3>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-xs sm:text-sm font-medium">Total Expenses</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2">₹{(reports.summary?.total_expenses || 0).toLocaleString()}</h3>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm font-medium">Net Profit</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2">{(reports.summary?.total_net_profit || 0).toLocaleString()}</h3>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs sm:text-sm font-medium">Total Settled</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2">{(reports.summary?.total_settled || 0).toLocaleString()}</h3>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Owner Entitlements</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Owner</TableHead>
                      <TableHead>Share %</TableHead>
                      <TableHead>Total Entitled</TableHead>
                      <TableHead>Total Paid</TableHead>
                      <TableHead>Pending</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.owners?.map((o, i) => (
                      <TableRow key={o.id || i}>
                        <TableCell className="font-semibold">{o.owner_name}</TableCell>
                        <TableCell>{o.share_percentage}%</TableCell>
                        <TableCell>{(o.total_entitled || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-green-600 font-medium">{(o.total_paid || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell className={o.pending > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                          {(o.pending || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>
                          <Badge variant={o.status === 'SETTLED' ? 'success' : 'destructive'}>{o.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profit Summaries</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Gross Profit (KP)</TableHead>
                      <TableHead>Expenses (₹)</TableHead>
                      <TableHead>Net Profit (KP)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.profit_summaries?.map((s, i) => (
                      <TableRow key={s.id || i}>
                        <TableCell className="font-medium">{s.from_date} → {s.to_date}</TableCell>
                        <TableCell className="text-green-600 font-semibold">{(s.gross_profit || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-red-600 font-semibold">₹{(s.total_expenses || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell className="font-bold text-blue-600">{(s.net_profit || 0).toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Settlement Records</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Owner</TableHead>
                      <TableHead>Profit Period</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Payment Mode</TableHead>
                      <TableHead>Paid On</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.settlements?.map((p, i) => (
                      <TableRow key={p.id || i}>
                        <TableCell className="font-semibold">{p.owner_name}</TableCell>
                        <TableCell>{p.profit_period}</TableCell>
                        <TableCell className="text-green-600 font-bold">{(p.payment_amount || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell><Badge variant="outline">{p.payment_mode}</Badge></TableCell>
                        <TableCell>{new Date(p.payment_date).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell className="text-gray-600">{p.remarks || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-900">💡 Calculation Logic (OPTION-K with Petty Cash Conversion)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="p-3 bg-white rounded-lg border border-blue-200">
                  <p className="font-bold text-blue-900 mb-1">Core Formula:</p>
                  <p className="font-mono text-sm">Net Profit = Gross Profit − (Total Expenses ÷ 1000)</p>
                  <p className="text-xs text-gray-600 mt-1">📌 1000 ₹ expense = 1 kitty point deduction</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-blue-200">
                  <p className="font-bold text-blue-900 mb-1">Owner Entitlement:</p>
                  <p className="font-mono text-sm">Entitled = Σ (Net Profit × Share % ÷ 100)</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-blue-200">
                  <p className="font-bold text-blue-900 mb-1">Settlement Tracking:</p>
                  <p className="font-mono text-sm">Pending = Entitled − Total Paid</p>
                </div>
                <div className="mt-4 space-y-1 text-green-700 font-medium">
                  <p>✓ Settlements never modify profit</p>
                  <p>✓ Profit never recalculated retroactively</p>
                  <p>✓ Petty cash automatically converted to kitty points</p>
                  <p>✓ Complete audit trail preserved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// Main Settlement Component with Tabs
const Settlement = () => {
  return (
    <LayoutWrapper
      title="Settlement Management"
      subtitle="Professional Profit & Owner Settlement System with Petty Cash Integration"
    >
      <div className="space-y-6">
        <SettlementDashboard />

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4" data-testid="settlement-tabs">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="settlements" data-testid="settlements-tab">Settlements</TabsTrigger>
            <TabsTrigger value="owners" data-testid="owners-tab">Owners</TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <ProfitSummaryManagement />
          </TabsContent>

          <TabsContent value="owners" className="mt-6">
            <OwnerManagement />
          </TabsContent>

          <TabsContent value="settlements" className="mt-6">
            <SettlementPayments />
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <SettlementReports />
          </TabsContent>
        </Tabs>
      </div>
    </LayoutWrapper>
  );
};

export default Settlement;
